/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines-per-function */
import { type Layer, type NormalLayerSpec, defineNormalLayer } from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { type Hook } from '../../../hook';
import { sortFlatTree, type TreeTableData } from '../../../util/data';

export type NormalTableData = { [k: string]: number | string };

type TableData = NormalTableData[] | TreeTableData[];

export type TableOption = {
  /** 配置原始数据 */
  data: TableData;
  /** 行表头 */
  rows: string[];
  /** 列表头 */
  columns: string[];
  /** 解析数据字段的元信息 */
  dataMetaInfo: ReturnType<Hook['parseDataMetaInfo']>;
};

export type CustomEvent = {
  data: {
    dataIndex: number;
    key: string;
    value: string | number;
  }[];
  selectedValue: string | number;
  selectedType: string;
};

type Event =
  | {
      type: string;
      data: CustomEvent;
    }
  | {
      type: string;
      page: number;
    }
  | {
      type: string;
      id: string;
    }
  | {
      type: string;
      isExpand: boolean;
    };

const visInfo: Layer['visInfo'] = {
  id: 'multiTable',
  name_en: 'MultiTable',
  // name_zh: '多维表格',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    rows: {
      itemType: [DEFINE.STR],
      category: DEFINE.ARRAY
    },
    columns: {
      itemType: [DEFINE.STR],
      category: DEFINE.ARRAY
    }
  }
};

const tableConfig = {
  defaultSplitNum: 2,
  maxHeight: 520,
  width: 570,
  pageSize: 10,
  currentPage: 1
};

export interface Spec extends NormalLayerSpec {
  /** 使用的数据集索引，默认为 0 */
  dataIndex?: number;
  encoding: {
    rows?: string[];
    columns?: string[];
  };
  customEvent?: (event: Event) => void;
  isTreeTable?: boolean;
  maxHeight?: number;
  width?: number;
  columnsNum?: number;
  pageSize?: number;
  currentPage?: number;
}

export const layer = defineNormalLayer<Spec>({
  type: visInfo.id,
  cdn: 'https://s.thsi.cn/cd/iwc-datav-standard-chart/aigc/0.15.1/table.umd.min.js',
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, ui, hook, token } = api;
    const {
      dataIndex = 0,
      encoding = {},
      customEvent,
      columnsNum = tableConfig.defaultSplitNum,
      maxHeight = tableConfig.maxHeight,
      width = tableConfig.width,
      pageSize = tableConfig.pageSize,
      currentPage = tableConfig.currentPage,
      isTreeTable = false
    } = spec;
    const { columns = [], rows = [] } = encoding;
    const tableStyle = {
      maxHeight,
      width
    };
    const data = dataArray[dataIndex].values;
    const dataMetaInfo = hook.parseDataMetaInfo(dataArray[dataIndex]);

    function onCustomEvent(event: Event) {
      if (customEvent) {
        customEvent(event);
      }
    }

    // 数据转换函数
    const createTableInstance = (
      tableType: string,
      transformedData: TableOption['data'],
      processedRows: string[],
      processedColumns: string[]
    ) => {
      const tableInstance = VisallTable.TableFactory.create($dom, {
        tableType,
        option: {
          data: transformedData,
          rows: processedRows,
          columns: processedColumns,
          dataMetaInfo,
          ...(tableType === 'horizontal' && { columnsNum })
        },
        formatData: hook.formatDataNumberValue,
        pagination: {
          pageSize,
          currentPage
        },
        token,
        tableStyle
      });

      // 处理事件监听
      if (tableInstance.on) {
        tableInstance.on('cell:selected', (...args: unknown[]) => {
          const event = args[0] as Event;
          onCustomEvent(event);
        });

        tableInstance.on('page:changed', (...args: unknown[]) => {
          const event = args[0] as Event;
          onCustomEvent(event);
        });

        tableInstance.on('tree:selected', (...args: unknown[]) => {
          const event = args[0] as Event;
          onCustomEvent(event);
        });

        tableInstance.on('cell:expand', (...args: unknown[]) => {
          const event = args[0] as Event;
          onCustomEvent(event);
        });
      }

      return () => {
        $dom.innerHTML = '';
        tableInstance.destroy();
      };
    };

    // 横向一维表格
    if (data.length === 1 && !columns.length && rows.length) {
      if (columnsNum % 2 !== 0) {
        throw new Error('Please input an event-numbered column');
      }
      return createTableInstance('horizontal', data, rows, columns);
    }

    // 纵向一维表格
    if (columns.length && !rows.length) {
      return createTableInstance('vertical', data, rows, columns);
    }

    // 第一列为额外表头的多维表格
    if (columns.length > 1 && rows.length === 1) {
      const { rowsData } = util.dataEncoding.encoding(data.slice(), {
        rowsData: {
          field: rows[0],
          transforms: [{ type: 'sort', order: 'ascending' }]
        }
      });
      const transformData = util.dataTransform.transform(data.slice(), [
        { type: 'sort', field: rows[0], order: 'descending' }
      ]);
      return createTableInstance('multiX', transformData, rowsData as string[], columns);
    }

    // 第一行为额外表头的多维表格
    if (columns.length === 1 && rows.length > 1 && !isTreeTable) {
      const { columnsData } = util.dataEncoding.encoding(data.slice(), {
        columnsData: {
          field: columns[0],
          transforms: [{ type: 'sort', order: 'descending' }]
        }
      });
      columnsData.unshift(columns[0]);
      const transformData = util.dataTransform.transform(data.slice(), [
        { type: 'sort', field: columns[0], order: 'descending' }
      ]);
      return createTableInstance('multiY', transformData, rows, columnsData as string[]);
    }

    // 树状表格
    if (isTreeTable) {
      const firstData = (data as unknown as TreeTableData[])[0];
      const sortedData = sortFlatTree(firstData);
      const treeTableKeys = Object.keys(sortedData);
      const treeTableColumns = treeTableKeys.filter(key => sortedData[key].columns);
      const treeTableRows = treeTableKeys.filter(key => sortedData[key].rows);
      const transformData = (data as unknown as TreeTableData[]).slice().sort((a, b) => {
        return (
          new Date(b[treeTableColumns[0]].value).getTime() -
          new Date(a[treeTableColumns[0]].value).getTime()
        );
      });
      const columnsData = transformData.map(item => {
        return item[treeTableColumns[0]].value;
      });
      columnsData.unshift(treeTableColumns[0]);
      return createTableInstance('tree', transformData, treeTableRows, columnsData as string[]);
    }

    // 如果没有匹配的条件，返回空的清理函数
    return () => {
      // eslint-disable-next-line no-param-reassign
      $dom.innerHTML = '';
    };
  }
});
